import { useState, useEffect, memo, useCallback } from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  Button,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { Add } from "@material-ui/icons";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { theme } from "../constants/theme";
import {
  GeneralModal,
  SortButtonAndMenu,
  FilterButtonAndMenu,
  Searchbar,
} from "../components-molecules";
import { ClusteredList } from "../components-organisms";
import { HeaderWithButtons } from "../components-molecules";
import { ButtonPrimary, CircularProggressIndicator, PrimaryButton } from "../components-atoms";
import { usePrevious } from "../utils";
import { clearSubCategorysSymbols } from "../redux/actions/getSubcategories";
import { getVerticalsSymbols } from "../redux/actions/getVerticals";
import {
  getCategoriesSymbols,
  addNewCategory,
  getCategoriesSymbolsWithParams,
  clearCategorysSymbols,
} from "../redux/actions/getCategories";
import { buttonHoverAndActive, textButtonHoverAndActive } from "../utils/styles/buttonStyles";

// import { setError } from "../redux/actions/error";

export const Categories = memo((props: any) => {
  const classes = useStyles();
  const [sortType, setSortType] = useState("Asc");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<{
    Verticals?: Array<string>;
  } | null>();
  const [verticalList, setVerticalList] = useState<Array<any>>(
    props?.Vertical ? [props.Vertical] : []
  );
  const [selectedVertical, setSelectedVertical] = useState<{
    Id: string;
    Name: String;
  } | null>();
  const [verticalError, setVerticalError] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Object | null>();
  const dispatch = useDispatch();
  const [openAddShopModal, setAddOpenShopModal] = useState<boolean>(false);
  const [categoryData, loading, verticalData] = useSelector((state: any) => [
    state.CategorySymbolsReducer.data,
    state.CategorySymbolsReducer.loading,
    state.VerticalSymbolsReducer.data,
  ]);

  const history = useHistory();
  const debounceDuration = 500;

  // useEffect(() => {
  //   console.log("Category data is : ", categoryData);
  //   console.log("props vertical changed : ", props?.Vertical);
  // }, [categoryData]);

  useEffect(() => {
    dispatch(clearCategorysSymbols());
    dispatch(clearSubCategorysSymbols());
    dispatch(getVerticalsSymbols());
    if (props?.Vertical) {
      if (props?.Vertical.Id === null || props?.Vertical.Id === undefined) {
        return;
      }
      dispatch(
        getCategoriesSymbolsWithParams(
          searchTerm ? searchTerm : "",
          sortType.trim() === "" ? "Asc" : sortType,
          { Verticals: [props?.Vertical.Id] }
        )
      );
      setFilter({ Verticals: [props?.Vertical.Id] });
    } else if (!props?.isUnderPage && !props?.Vertical) {
      dispatch(getCategoriesSymbols());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.Vertical]);

  useEffect(() => {
    const verticalsAsOneArray: Array<any> = [];
    verticalData.forEach((element: Array<any>) => {
      if (element?.[1] && element[1].length > 0) {
        element[1].forEach((item: any) => verticalsAsOneArray.push(item));
      }
    });
    setVerticalList(verticalsAsOneArray);
    props?.Vertical
      ? setSelectedVertical(props.Vertical)
      : setSelectedVertical(verticalsAsOneArray?.[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verticalData]);

  useEffect(() => {
    if (searchTerm) {
      debounce(() => {
        dispatch(
          getCategoriesSymbolsWithParams(
            searchTerm,
            sortType.trim() === "" ? "Asc" : sortType,
            filter
          )
        );
      }, debounceDuration)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const prevVertical: any = usePrevious(props?.Vertical);
  useEffect(() => {
    if (prevVertical && prevVertical.Id === props?.Vertical?.Id) return;
    if (filter === null || filter === undefined) {
      return;
    }
    ((props?.Vertical && props?.isUnderPage) ||
      (!props?.Vertical && !props?.isUnderPage)) &&
      dispatch(
        getCategoriesSymbolsWithParams(
          searchTerm ? searchTerm : "",
          sortType.trim() === "" ? "Asc" : sortType,
          filter
        )
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortType, filter, prevVertical]);

  const handleSubmit = () => {
    if (newCategoryName.trim() === "") {
      setNameError(true);
      return;
    }
    if (!selectedVertical || !selectedVertical?.Id) {
      setVerticalError(true);
      return;
    }
    const body = new FormData();

    body.append(
      "json",
      JSON.stringify({
        Name: newCategoryName,
        Description: "",
        VerticalId: props?.Vertical
          ? props?.Vertical?.Id
          : selectedVertical?.Id,
      })
    );
    dispatch(
      addNewCategory(body, null, (resp) => {
        setNewCategoryName("");
        setSelectedVertical(props?.Vertical ?? null);
        setAddOpenShopModal(false);
        setVerticalError(false);
        history.push(`/admin/category/${resp.Result}`);
        if (props?.Vertical) {
          dispatch(
            getCategoriesSymbolsWithParams(
              searchTerm ? searchTerm : "",
              sortType.trim() === "" ? "Asc" : sortType,
              { Verticals: [props?.Vertical.Id] }
            )
          );
        } else {
          dispatch(getCategoriesSymbols());
        }
      })
    );
  };

  const bottomTextCreator = useCallback(
    (item: any) =>
      `${item?.VerticalName ?? "(Unnamed Vertical)"} · ${
        item?.SubCategoryCount ?? 0
      } ${item?.SubCategoryCount === 1 ? "Sub Category" : "Sub Categories"} · ${
        item?.ProductCount ?? 0
      } ${item?.ProductCount === 1 ? "Product" : "Products"}`,
    []
  );

  const topTextCreator = useCallback(
    (item: any) => `${item?.Title ?? "Unnamed Category"}`,
    []
  );

  const pathCreator = useCallback(
    (item: any) => `/admin/category/${item?.Id}`,
    []
  );

  const updateList = ({ search, order, filter }) => {
    dispatch(
      getCategoriesSymbolsWithParams(search ? search : "", order, filter)
    );
  };

  return (
    <div className={classes.container}>
      <CssBaseline />
      <div className={classes.root}>
        <HeaderWithButtons title="">
          <HeaderWithButtons.ButtonArea>
            <div className={classes.mgLeftAuto}>
              <SortButtonAndMenu
                menuItems={[
                  {
                    title: "Alphabetical ascending",
                    value: "Asc",
                    handleSelect: () => {
                      setSortType("Asc");
                      updateList({
                        search: "",
                        order: "Asc",
                        filter: filter,
                      });
                    },
                  },
                  {
                    title: "Alphabetical descending",
                    value: "Desc",
                    handleSelect: () => {
                      setSortType("Desc");
                      updateList({
                        search: "",
                        order: "Desc",
                        filter: filter,
                      });
                    },
                  },
                ]}
                selectedSortType={sortType}
              />
              {!props?.Vertical && (
                <FilterButtonAndMenu
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  addRemoveFilter={(newFilters: any) => {
                    setFilter(newFilters);
                  }}
                  setFilterTypeSelected={() => {}}
                  filterTypeDataDetails={verticalData}
                  filterTypeList={[
                    { title: "Verticals", typeKey: "Verticals" },
                  ]}
                />
              )}
              <IconButton
                data-automation="search-button"
                className={classes.searchIconButton}
                color="primary"
                onClick={() => setOpenSearch(true)}
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
              <ButtonPrimary
                data-automation="add-vertical-button"
                variant="contained"
                color="primary"
                disableElevation
                className={classes.primaryButton}
                style={{
                  paddingLeft: props.loading ? "12px" : "16px",
                }}
                startIcon={<Add />}
                onClick={() => {
                  setAddOpenShopModal(true);
                }}
              >
                Add Category
              </ButtonPrimary>
            </div>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        <Searchbar
          query={searchTerm}
          isOpen={openSearch}
          placeholder="Search Category"
          onCancel={() => {
            setOpenSearch(false);
            setSearchTerm("");
            updateList({
              search: "",
              order: sortType,
              filter: filter,
            });
          }}
          onChange={(event) =>
            debounce(
              (e) => setSearchTerm(e.target.value),
              debounceDuration
            )(event)
          }
        />
        {loading ? (
          <>
            <CircularProggressIndicator />
          </>
        ) : (
          <ClusteredList
            data={categoryData ?? []}
            bottomTextCreator={bottomTextCreator}
            topTextCreator={topTextCreator}
            pathCreator={pathCreator}
            headlineTypeSingular="Category"
            headLineTypePlural="Categories"
          />
        )}
      </div>
      <GeneralModal
        title="Add Category"
        description="Give category a name. Once created you will be able to edit category information and add sub categories."
        visible={openAddShopModal}
        onClose={setAddOpenShopModal}
        //onSubmit={handleSubmit}
        modalWidth="560px"
      >
        <GeneralModal.ContentArea>
          <Grid container>
            {!props?.Vertical ? (
              <Grid item xs={12}>
                <FormControl
                  variant="outlined"
                  className={classes.selectArea}
                  error={verticalError}
                  disabled={props?.Vertical ? true : false}
                >
                  <InputLabel id="demo-simple-select-outlined-label">
                    Vertical
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    value={
                      props?.Vertical
                        ? props.Vertical.Id
                        : selectedVertical
                        ? selectedVertical?.Id
                        : verticalList && verticalList?.[0]
                        ? verticalList[0].Id
                        : ""
                    }
                    onChange={(event: any) => {
                      if (props?.Vertical) return;
                      setSelectedVertical(
                        verticalList?.find(
                          (item: any) => item.Id === event.target.value
                        )
                      );
                      //setSelectedVertical();
                    }}
                    label="Vertical"
                  >
                    {verticalList &&
                      verticalList.map((v: any, index: number) => (
                        <MenuItem key={index} value={v.Id}>
                          {v.Name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <TextField
                placeholder="Category Name"
                value={newCategoryName}
                variant="outlined"
                margin="normal"
                fullWidth
                id="CategoryName"
                label="Category Name"
                name="CategoryName"
                onChange={(event: any) =>
                  setNewCategoryName(event.target.value)
                }
                error={nameError}
                InputProps={{
                  classes: {
                    root: classes.cssOutlinedInput,
                    focused: classes.cssFocused,
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
            </Grid>
          </Grid>
        </GeneralModal.ContentArea>
        <GeneralModal.ButtonArea>
          <Button
            onClick={() => {
              setAddOpenShopModal(false);
            }}
            className={classes.cancelButton}
          >
            <Typography
              onClick={() => {
                setNameError(false);
                setNewCategoryName("");
                setSelectedVertical(props?.Vertical);
                setAddOpenShopModal(false);
                setVerticalError(false);
              }}
              variant="button"
              component="p"
              color="primary"
            >
              Cancel
            </Typography>
          </Button>
          <PrimaryButton
            disabled={!newCategoryName}
            onClick={handleSubmit}
            title="Submit"
          />
        </GeneralModal.ButtonArea>
      </GeneralModal>
    </div>
  );
});

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchIconButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    ...textButtonHoverAndActive
  },
  root: {
    flexGrow: 1,
    maxWidth: "840px",
  },
  mgLeftAuto: {
    display: "flex",
    flexDirection: "row",
    paddingRight: 24,
  },
  cancelButton: {
    width: "80px",
    height: "48px",
    borderRadius: "8px",
    padding: "6px 16px",
    marginRight: "8px",
    ...textButtonHoverAndActive
  },
  cssOutlinedInput: {
    display: "flex",
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,

    },
  },

  cssFocused: {},

  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive
  },
  selectArea: { width: "100%", marginTop: 16, marginBottom: 15 },
});
